import apiClient from './axios.config';

const inflightGetRequests = new Map<string, Promise<any>>();
const recentGetResponses = new Map<string, { expiresAt: number; value: any }>();
const RECENT_GET_TTL_MS = 1500;

async function dedupeGet<T>(key: string, loader: () => Promise<T>): Promise<T> {
  const now = Date.now();
  const recent = recentGetResponses.get(key);
  if (recent && recent.expiresAt > now) {
    return recent.value as T;
  }

  const inflight = inflightGetRequests.get(key);
  if (inflight) {
    return inflight as Promise<T>;
  }

  const request = loader()
    .then((value) => {
      recentGetResponses.set(key, {
        value,
        expiresAt: Date.now() + RECENT_GET_TTL_MS,
      });
      return value;
    })
    .finally(() => {
      inflightGetRequests.delete(key);
    });

  inflightGetRequests.set(key, request);
  return request;
}

export type ViewType = 'TABLE' | 'CARDS' | 'MAP';

export interface DepotResponse {
  id: string;
  tenantId: string;
  name: string;
  modelType: string;
  configFilesMap: Record<string, string>;
  active: boolean;
  createdAtEpoch: number;
  updatedAtEpoch: number;
  createdBy: string;
  updatedBy: string;
}

export interface RoutingJobResponse {
  id: string;
  depotId: string;
  inputFilesMap: string;
  outputFilesMap: string;
  createdAtEpoch: number;
  updatedAtEpoch: number;
  createdBy: string;
  status: string;
  metrics?: Record<string, number>;
  depotSnapshot: DepotResponse;
  errorInfo?: string;
  tripIds?: string[];
  tripsCreatedAtEpoch?: number;
  tripsCreatedBy?: string;
  tripDispatchDate?: string;
  tripTimezone?: string;
}

export type TripStatus = 'CREATED';
export type TripGeometryQuality = 'COMPLETE' | 'PARTIAL' | 'MISSING';

export interface PlannedTouchPointResponse {
  touchPointId: string;
  routeGeometryNodeKey: string;
  sequence: number;
  latitude: number;
  longitude: number;
  plannedArrivalAtEpoch: number;
  plannedAttemptAtEpoch: number;
  plannedDepartureAtEpoch: number;
  plannedArrivalClock: string;
  plannedAttemptClock: string;
  plannedDepartureClock: string;
  buyerName?: string;
  address?: string;
  pinCode?: string;
  slotTag?: string;
  clusterId?: string;
  dropWeightKg?: number;
  dropVolumeCc?: number;
  gmvInRs?: number;
  labels?: Record<string, string>;
}

export interface PlannedRouteNodeResponse {
  nodeKey: string;
  latitude: number;
  longitude: number;
}

export interface PlannedRouteSegmentResponse {
  sequence: number;
  fromNodeKey: string;
  toNodeKey: string;
  geometryPolyline6?: string;
}

export interface TripResponse {
  schemaVersion: number;
  id: string;
  tenantId: string;
  depotId: string;
  routingTaskId: string;
  plannedRouteId: string;
  status: TripStatus;
  dispatchDate: string;
  timezone: string;
  createdAtEpoch: number;
  updatedAtEpoch: number;
  createdBy: string;
  plannedDepartureAtEpoch: number;
  plannedReturnAtEpoch: number;
  plannedDepartureClock: string;
  plannedReturnClock: string;
  driver: { id: string; name: string; vendorName?: string };
  vehicle: { id: string; number: string; type: string };
  zoneId?: string;
  slots: string[];
  metrics: Record<string, number>;
  availableCapacity: Record<string, number>;
  breaches: Record<string, number>;
  touchPoints: PlannedTouchPointResponse[];
  plannedRoute: {
    orderedNodes: PlannedRouteNodeResponse[];
    segments: PlannedRouteSegmentResponse[];
    geometryQuality: TripGeometryQuality;
  };
}

export interface TripSummaryResponse {
  id: string;
  routingTaskId: string;
  plannedRouteId: string;
  status: TripStatus;
  dispatchDate: string;
  timezone: string;
  plannedDepartureAtEpoch: number;
  plannedReturnAtEpoch: number;
  driverName: string;
  vehicleNumber: string;
  vehicleType: string;
  touchPointCount: number;
  distanceKm?: number;
  weightKg?: number;
  geometryQuality: TripGeometryQuality;
}

export interface CreateTripsResponse {
  routingTaskId: string;
  depotId: string;
  dispatchDate: string;
  timezone: string;
  tripCount: number;
  tripIds: string[];
}

export interface ViewFieldDefinition {
  id: string;
  label: string;
  field: string;
  format?: string;
  visible?: boolean;
}

export interface ViewMapDefinition {
  latField: string;
  lngField: string;
  groupField?: string;
  tooltipFields?: string[];
}

export interface ViewDefinition {
  viewId: string;
  title: string;
  type: ViewType;
  dataSource: string;
  columns?: ViewFieldDefinition[];
  cards?: ViewFieldDefinition[];
  map?: ViewMapDefinition;
}

export interface RoutingJobViewResponse {
  viewId: string;
  schema: ViewDefinition;
  items: Array<Record<string, any>>;
  summary?: Record<string, any>;
}

export interface RoutingJobWorkspaceResponse {
  job: RoutingJobResponse;
  views: RoutingJobViewResponse[];
}

export interface FileUploadResponse {
  fileName: string;
  storageId: string;
  contentType: string;
  size: number;
  storagePath?: string | null;
}

export interface ReplaceRoutingJobFileRequest {
  fileType: string;
  storageId: string;
  source: 'input' | 'output';
}

export interface FileArchiveEntryRequest {
  fileId: string;
  fileName: string;
}

export interface FileArchiveRequest {
  archiveName?: string;
  files: FileArchiveEntryRequest[];
}

export interface TripEditRequest {
  touchPointIds: string[];
  destinationTripId: string;
}

export interface TripEditResponse {
  requestAccepted: boolean;
  message: string;
  job: RoutingJobResponse;
  views: RoutingJobViewResponse[];
}

export interface RouteGeometryRefreshResponse {
  message: string;
  requiredArcCount: number;
  existingArcCount: number;
  resolvedArcCount: number;
  failedArcCount: number;
  deferredArcCount: number;
}

export interface GeoLocationCorrectionRequest {
  screen: 'verify' | 'edit';
  touchPointRef: string;
  locationType?: 'PICKUP' | 'DROP' | 'ORIGIN';
  latitude: number;
  longitude: number;
}

export interface GeoLocationCorrectionResponse {
  requestAccepted: boolean;
  message: string;
  job: RoutingJobResponse;
  views: RoutingJobViewResponse[];
}

export const routingJobsApi = {
  async listDepots(): Promise<DepotResponse[]> {
    return dedupeGet('GET:/v1/users/me/depots', async () => {
      const response = await apiClient.get<DepotResponse[]>('/v1/users/me/depots');
      return response.data ?? [];
    });
  },

  async listTasks(depotId: string): Promise<RoutingJobResponse[]> {
    return dedupeGet(`GET:/routing-job/depot/${depotId}/tasks?limit=20`, async () => {
      const response = await apiClient.get<RoutingJobResponse[]>(`/routing-job/depot/${depotId}/tasks`, {
        params: { limit: 20 },
      });
      return response.data ?? [];
    });
  },

  async getJob(depotId: string, jobId: string): Promise<RoutingJobResponse> {
    return dedupeGet(`GET:/routing-job/depot/${depotId}/task/${jobId}`, async () => {
      const response = await apiClient.get<RoutingJobResponse>(`/routing-job/depot/${depotId}/task/${jobId}`);
      return response.data;
    });
  },

  async getView(
    depotId: string,
    jobId: string,
    viewId: string,
    outputMode: 'edited' | 'original' = 'edited'
  ): Promise<RoutingJobViewResponse> {
    return dedupeGet(`GET:/v1/view/depot/${depotId}/job/${jobId}/views/${viewId}?outputMode=${outputMode}`, async () => {
      const response = await apiClient.get<RoutingJobViewResponse>(
        `/v1/view/depot/${depotId}/job/${jobId}/views/${viewId}`,
        {
          params: { outputMode },
        }
      );
      return response.data;
    });
  },

  async getVerifyWorkspace(
    depotId: string,
    jobId: string,
    outputMode: 'edited' | 'original' = 'edited'
  ): Promise<RoutingJobWorkspaceResponse> {
    return dedupeGet(`GET:/v1/view/depot/${depotId}/job/${jobId}/verify-workspace?outputMode=${outputMode}`, async () => {
      const response = await apiClient.get<RoutingJobWorkspaceResponse>(
        `/v1/view/depot/${depotId}/job/${jobId}/verify-workspace`,
        {
          params: { outputMode },
        }
      );
      return response.data;
    });
  },

  async verifyJob(depotId: string, jobId: string): Promise<void> {
    await apiClient.post(`/routing-job/tasks/depot/${depotId}/job/${jobId}/verify`);
  },

  async rerunJob(depotId: string, jobId: string): Promise<void> {
    await apiClient.post(`/routing-job/tasks/depot/${depotId}/job/${jobId}/rerun`);
  },

  async createDraftTask(
    depotId: string,
    inputFiles: Record<string, string>
  ): Promise<RoutingJobResponse> {
    const response = await apiClient.post<RoutingJobResponse>(`/routing-job/depots/${depotId}/tasks`, {
      inputFiles,
    });
    return response.data;
  },

  async uploadTenantFile(
    tenantId: string,
    modelType: string,
    fileType: string,
    file: File
  ): Promise<FileUploadResponse> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('tenantId', tenantId);
    formData.append('modelType', modelType);
    formData.append('fileType', fileType);
    const response = await apiClient.post<FileUploadResponse>('/file/tenant/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  async downloadInternalFile(fileId: string): Promise<Blob> {
    const response = await apiClient.get('/internal/files', {
      params: { fileId },
      responseType: 'blob',
    });
    return response.data as Blob;
  },

  async downloadInternalFilesArchive(request: FileArchiveRequest): Promise<Blob> {
    const response = await apiClient.post('/internal/files/archive', request, {
      responseType: 'blob',
    });
    return response.data as Blob;
  },

  async replaceJobFile(
    depotId: string,
    jobId: string,
    request: ReplaceRoutingJobFileRequest
  ): Promise<RoutingJobResponse> {
    const response = await apiClient.put<RoutingJobResponse>(
      `/routing-job/depot/${depotId}/task/${jobId}/files`,
      request
    );
    return response.data;
  },

  async editTrip(
    depotId: string,
    jobId: string,
    request: TripEditRequest
  ): Promise<TripEditResponse> {
    const response = await apiClient.post<TripEditResponse>(
      `/routing-job/depot/${depotId}/task/${jobId}/edit`,
      request
    );
    return response.data;
  },

  async getRouteGeometry(depotId: string, jobId: string): Promise<string> {
    const response = await apiClient.get<string>(
      `/routing-job/depot/${depotId}/task/${jobId}/route-geometry`,
      { responseType: 'text' }
    );
    return response.data ?? '';
  },

  async refreshRouteGeometry(
    depotId: string,
    jobId: string,
    outputMode: 'edited' | 'original'
  ): Promise<RouteGeometryRefreshResponse> {
    const response = await apiClient.post<RouteGeometryRefreshResponse>(
      `/routing-job/depot/${depotId}/task/${jobId}/route-geometry/refresh`,
      { outputMode }
    );
    return response.data;
  },

  async correctGeoLocation(
    depotId: string,
    jobId: string,
    request: GeoLocationCorrectionRequest
  ): Promise<GeoLocationCorrectionResponse> {
    const response = await apiClient.post<GeoLocationCorrectionResponse>(
      `/routing-job/depot/${depotId}/task/${jobId}/geo-correction`,
      request
    );
    return response.data;
  },

  async createTrips(
    depotId: string,
    taskId: string,
    dispatchDate: string
  ): Promise<CreateTripsResponse> {
    const response = await apiClient.post<CreateTripsResponse>(
      `/v1/depots/${depotId}/routing-tasks/${taskId}/trips`,
      { dispatchDate }
    );
    return response.data;
  },

  async listTrips(
    depotId: string,
    dispatchDate: string,
    status: TripStatus = 'CREATED'
  ): Promise<TripSummaryResponse[]> {
    const response = await apiClient.get<TripSummaryResponse[]>(`/v1/depots/${depotId}/trips`, {
      params: { dispatchDate, status },
    });
    return response.data ?? [];
  },

  async getTrip(depotId: string, tripId: string): Promise<TripResponse> {
    const response = await apiClient.get<TripResponse>(`/v1/depots/${depotId}/trips/${tripId}`);
    return response.data;
  },
};
